/**
 * MyProfile — SPL/Admin self-profile editing page.
 * Route: /admin/profile
 *
 * Allows operators to edit their own name, phone, timezone, bio, linkedin.
 * Email is read-only (managed by auth). Avatar uses initials with future
 * support for profile_photo_url.
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { safeMutation, safeQuery, sanitizeErrorMessage, generateErrorId } from '../../utils/safeSupabase';
import { getTagColor } from '../../constants/tags';
import { useConfirm } from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import {
  requestAccountDeletion,
  cancelDeletionRequest,
  getOwnPendingDeletionRequest,
  exportMyData,
  downloadExportBundle,
  // Phase 11 slot 4 — Articles 16 + 18
  requestRectification,
  listOwnRectificationRequests,
  requestProcessingRestriction,
  getOwnProcessingRestriction,
} from '../../services/gdprService';

// Phase 11 slot 4 — fields a user can request rectification on. Mirrors the
// server-side allowlist in `process_rectification_request` migration 258.
// Fields are limited to identity-data per Art 16 ("data accuracy"); analytical /
// billing fields are NOT rectifiable via this flow.
const RECTIFICATION_ALLOWED_FIELDS = [
  { value: 'name',             label: 'Full name' },
  { value: 'email',            label: 'Email' },
  { value: 'phone',            label: 'Phone' },
  { value: 'bio',              label: 'Bio' },
  { value: 'location_country', label: 'Country' },
  { value: 'location_state',   label: 'State / region' },
  { value: 'location_city',    label: 'City' },
  { value: 'linkedin_url',     label: 'LinkedIn URL' },
  { value: 'github_url',       label: 'GitHub URL' },
  { value: 'portfolio_url',    label: 'Portfolio URL' },
  { value: 'website',          label: 'Website' },
  { value: 'date_of_birth',    label: 'Date of birth' },
  { value: 'languages',        label: 'Languages' },
  { value: 'timezone',         label: 'Timezone' },
];

const TIMEZONES = Intl.supportedValuesOf ? Intl.supportedValuesOf('timeZone').filter(tz =>
  tz.startsWith('Africa/') || tz.startsWith('America/') || tz.startsWith('Asia/') ||
  tz.startsWith('Europe/') || tz.startsWith('Pacific/') || tz.startsWith('Australia/')
) : ['UTC', 'Africa/Nairobi', 'America/New_York', 'America/Los_Angeles', 'Europe/London', 'Asia/Kolkata'];

export default function MyProfile() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const toast = useToast(); // Phase 13.1.3 — replace alert() with toast
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveResult, setSaveResult] = useState(null); // 'saved' | 'error' | null
  const [form, setForm] = useState({
    name: '', phone: '', timezone: '', bio: '', linkedin_url: '',
  });
  const [pendingDeletion, setPendingDeletion] = useState(null);
  const [deletionBusy, setDeletionBusy] = useState(false);
  const [deletionReason, setDeletionReason] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportFeedback, setExportFeedback] = useState(null);
  // Phase 11 slot 4 — Article 16 + 18
  const [rectFieldName, setRectFieldName] = useState('');
  const [rectRequested, setRectRequested] = useState('');
  const [rectReason, setRectReason] = useState('');
  const [rectBusy, setRectBusy] = useState(false);
  const [rectFeedback, setRectFeedback] = useState(null);
  const [ownRectifications, setOwnRectifications] = useState([]);
  const [restriction, setRestriction] = useState(null);
  const [restrictionReason, setRestrictionReason] = useState('');
  const [restrictionBusy, setRestrictionBusy] = useState(false);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    safeQuery(
      supabase.from('individual').select('name, email, phone, timezone, bio, linkedin_url, primary_domain, profile_photo_url, role').eq('id', user.id).single(),
      'myProfile-fetch'
    ).then(({ data }) => {
      if (data) {
        setProfile(data);
        setForm({
          name: data.name || '',
          phone: data.phone || '',
          timezone: data.timezone || '',
          bio: data.bio || '',
          linkedin_url: data.linkedin_url || '',
        });
      }
      setLoading(false);
    });
    getOwnPendingDeletionRequest().then(setPendingDeletion);
    // Phase 11 slot 4 — load own rectification queue + restriction status
    listOwnRectificationRequests().then(setOwnRectifications);
    getOwnProcessingRestriction().then(setRestriction);
  }, [user?.id]);

  // Phase 11 slot 4 — Article 16 submit
  const handleSubmitRectification = async () => {
    if (rectBusy || !rectFieldName || !rectRequested.trim()) return;
    setRectBusy(true);
    setRectFeedback(null);
    try {
      const currentValue = profile?.[rectFieldName] ?? '';
      const result = await requestRectification({
        fieldName:      rectFieldName,
        currentValue:   String(currentValue),
        requestedValue: rectRequested.trim(),
        reason:         rectReason.trim() || null,
      });
      setOwnRectifications(await listOwnRectificationRequests());
      setRectFeedback({ ok: true, msg: `Submitted (request ${result?.request_id?.slice(0, 8)}…). Admin will review.` });
      setRectFieldName('');
      setRectRequested('');
      setRectReason('');
    } catch (e) {
      setRectFeedback({ ok: false, msg: e?.message || 'Submit failed. Try again.' });
    } finally {
      setRectBusy(false);
      setTimeout(() => setRectFeedback(null), 8000);
    }
  };

  // Phase 11 slot 4 — Article 18 request
  const handleRequestRestriction = async () => {
    if (restrictionBusy) return;
    const ok = await confirm({
      title: 'Request restriction of processing?',
      message:
        'GDPR Article 18 — your data stays here but admin should NOT process it ' +
        'outside the legal basis you grant (e.g., during a dispute). Useful when ' +
        'a record is contested but you do not want it deleted yet. An admin will ' +
        'see this on their queue and honor the restriction manually.',
      confirmText: 'Request restriction',
      cancelText: 'Not now',
    });
    if (!ok) return;
    setRestrictionBusy(true);
    try {
      await requestProcessingRestriction({ reason: restrictionReason.trim() || null });
      setRestriction(await getOwnProcessingRestriction());
      setRestrictionReason('');
    } catch (e) {
      { const errorId = generateErrorId(); console.error(`[MyProfile:restriction errorId=${errorId}]`, e); toast.error('Could not submit restriction: ' + sanitizeErrorMessage(e, { errorId })); }
    } finally {
      setRestrictionBusy(false);
    }
  };

  const handleRequestDeletion = async () => {
    if (deletionBusy) return;
    const ok = await confirm({
      title: 'Request account deletion?',
      message:
        'Your account will be scheduled for deletion. A Juuni SPL contacts you to confirm, ' +
        'then hard-deletes after a 30-day grace period. You can cancel any time before processing starts. ' +
        'Your projects, time entries, earnings, and assignments will all be removed.',
      confirmText: 'Request deletion',
      cancelText: 'Not now',
      destructive: true,
    });
    if (!ok) return;
    setDeletionBusy(true);
    try {
      await requestAccountDeletion({ reason: deletionReason });
      const fresh = await getOwnPendingDeletionRequest();
      setPendingDeletion(fresh);
      setDeletionReason('');
    } catch (e) {
      { const errorId = generateErrorId(); console.error(`[MyProfile:submitRequest errorId=${errorId}]`, e); toast.error('Could not submit request: ' + sanitizeErrorMessage(e, { errorId })); }
    } finally {
      setDeletionBusy(false);
    }
  };

  const handleExportMyData = async () => {
    if (exporting) return;
    setExporting(true);
    setExportFeedback(null);
    try {
      const bundle = await exportMyData();
      const subjectName = (bundle?.self?.name || bundle?.export_metadata?.subject_email || 'me').toString();

      // Phase 11 slot 4 — empty-export UX sentinel (cross-phase sweep audit 3.3).
      // If every operational array is empty (e.g., a freshly-signed-up user),
      // tell them up front rather than handing them a near-empty download.
      const operationalRowCount =
        (bundle?.applications?.length || 0)
        + (bundle?.assignments?.length || 0)
        + (bundle?.time_entries?.length || 0)
        + (bundle?.transactions?.length || 0)
        + (bundle?.payouts?.length || 0);

      const filename = downloadExportBundle(bundle, subjectName);
      // Phase 23.3 (adversarial use case discovery 2026-05-01) — tighten the
      // empty-export sentinel. Pre-fix wording said "no operational data
      // YET" which assumes the caller is an annotator who will accrue data
      // over time. For SPL / admin / god roles, the operational arrays
      // (applications, assignments-as-annotator, time_entries, transactions,
      // payouts) are always empty because those columns track the user as a
      // *worker*, not as a manager. The "yet" implied a future the SPL will
      // never have — confused dogfood SPL who scored batches + managed 4
      // active projects but saw "no operational data yet" on her self-
      // export. Role-neutral phrasing handles both cases honestly.
      const sentinel = operationalRowCount === 0
        ? ' (no annotator-side records found — only your profile is in the export)'
        : '';
      setExportFeedback({ ok: true, msg: `Downloaded ${filename}${sentinel}` });
    } catch (e) {
      setExportFeedback({ ok: false, msg: e?.message || 'Export failed. Try again.' });
    } finally {
      setExporting(false);
      setTimeout(() => setExportFeedback(null), 8000);
    }
  };

  const handleCancelDeletion = async () => {
    if (!pendingDeletion || deletionBusy) return;
    const ok = await confirm({
      title: 'Cancel your deletion request?',
      message: 'Your account stays active. You can request again at any time.',
      confirmText: 'Cancel deletion',
      cancelText: 'Keep pending',
    });
    if (!ok) return;
    setDeletionBusy(true);
    try {
      await cancelDeletionRequest(pendingDeletion.id);
      setPendingDeletion(null);
    } catch (e) {
      { const errorId = generateErrorId(); console.error(`[MyProfile:cancel errorId=${errorId}]`, e); toast.error('Could not cancel: ' + sanitizeErrorMessage(e, { errorId })); }
    } finally {
      setDeletionBusy(false);
    }
  };

  const handleSave = async () => {
    if (!user?.id || saving) return;
    setSaving(true);
    setSaveResult(null);

    const updates = {
      name: form.name.trim() || null,
      phone: form.phone.trim() || null,
      timezone: form.timezone || null,
      bio: form.bio.trim() || null,
      linkedin_url: form.linkedin_url.trim() || null,
      updated_at: new Date().toISOString(),
    };

    const { error } = await safeMutation(
      supabase.from('individual').update(updates).eq('id', user.id),
      'myProfile-save'
    );

    setSaving(false);
    if (error) {
      setSaveResult('error');
      setTimeout(() => setSaveResult(null), 4000);
    } else {
      setProfile(prev => ({ ...prev, ...updates }));
      setSaveResult('saved');
      setTimeout(() => setSaveResult(null), 3000);
    }
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const lbl = { display: 'block', fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 };
  const inp = { width: '100%', padding: '8px 12px', fontSize: 13, fontFamily: 'var(--font-mono)', background: 'var(--bg-input)', color: 'var(--text-primary)', border: '1px solid var(--border)', borderRadius: 6, outline: 'none', boxSizing: 'border-box' };

  if (loading) return <div style={{ padding: 40, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>Loading profile...</div>;
  if (!profile) return <div style={{ padding: 40, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)' }}>Profile not found. Contact support.</div>;

  // Avatar
  const name = profile.name || user?.email?.split('@')[0] || '';
  const initials = name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() || '?';
  const bgColor = getTagColor(profile.primary_domain) || 'var(--text-muted)';
  const roleLabel = profile.role === 'spl' ? 'Project Lead' : profile.role === 'admin' ? 'Admin' : profile.role === 'god' ? 'Founder' : profile.role === 'annotator' ? 'Annotator' : 'Team Member';

  return (
    <div style={{ maxWidth: 560, padding: '16px 24px 40px' }}>
      {/* Header — avatar + name + role */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 28 }}>
        <div style={{
          width: 48, height: 48, borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: profile.profile_photo_url ? 'transparent' : bgColor,
          overflow: 'hidden',
        }}>
          {profile.profile_photo_url ? (
            <img src={profile.profile_photo_url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <span style={{ fontSize: 16, fontWeight: 600, color: '#fff', fontFamily: 'var(--font-mono)' }}>{initials}</span>
          )}
        </div>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-sans)', margin: 0 }}>
            {profile.name || 'Your Profile'}
          </h1>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 4 }}>
            <span style={{ fontSize: 12, fontFamily: 'var(--font-mono)', padding: '3px 10px', whiteSpace: 'nowrap', borderRadius: 4, background: 'rgba(59,111,214,0.08)', color: '#3B6FD6' }}>{roleLabel}</span>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{profile.email || user?.email}</span>
          </div>
        </div>
      </div>

      {/* Personal Info */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Personal Information</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label htmlFor="my-profile-name" style={lbl}>Full Name</label>
            <input id="my-profile-name" type="text" value={form.name} onChange={e => updateField('name', e.target.value)} placeholder="Your name" style={inp} />
          </div>
          <div>
            <label htmlFor="my-profile-email" style={lbl}>Email</label>
            <input id="my-profile-email" type="email" value={profile.email || user?.email || ''} disabled style={{ ...inp, opacity: 0.5, cursor: 'not-allowed' }} />
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>Managed by authentication</div>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
          <div>
            <label htmlFor="my-profile-phone" style={lbl}>Phone</label>
            <input id="my-profile-phone" type="tel" value={form.phone} onChange={e => updateField('phone', e.target.value)} placeholder="+1 555-000-0000" style={inp} />
          </div>
          <div>
            <label htmlFor="my-profile-timezone" style={lbl}>Timezone</label>
            <select id="my-profile-timezone" value={form.timezone} onChange={e => updateField('timezone', e.target.value)} style={inp}>
              <option value="">Select timezone</option>
              {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>)}
            </select>
          </div>
        </div>
      </div>

      {/* Bio */}
      <div style={{ marginBottom: 24 }}>
        <label htmlFor="my-profile-bio" style={lbl}>Bio</label>
        <textarea id="my-profile-bio" value={form.bio} onChange={e => updateField('bio', e.target.value)} placeholder="A short description about yourself..." rows={3} style={{ ...inp, resize: 'vertical' }} />
      </div>

      {/* Links */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 12, paddingBottom: 6, borderBottom: '1px solid var(--border)' }}>Links</div>
        <div>
          <label htmlFor="my-profile-linkedin" style={lbl}>LinkedIn</label>
          <input id="my-profile-linkedin" type="url" value={form.linkedin_url} onChange={e => updateField('linkedin_url', e.target.value)} placeholder="https://linkedin.com/in/yourname" style={inp} />
        </div>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={handleSave} disabled={saving} style={{
          padding: '8px 24px', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600,
          background: saving ? 'var(--text-muted)' : 'var(--text-primary)',
          color: 'var(--bg)', border: 'none', borderRadius: 6, cursor: saving ? 'wait' : 'pointer',
        }}>
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
        {saveResult === 'saved' && <span style={{ fontSize: 12, color: '#5DCAA5', fontFamily: 'var(--font-mono)' }}>Saved</span>}
        {saveResult === 'error' && <span style={{ fontSize: 12, color: '#DC2626', fontFamily: 'var(--font-mono)' }}>Failed to save. Try again.</span>}
      </div>

      {/* Your data — GDPR Article 15 self-export */}
      <div style={{
        marginTop: 40,
        padding: 20,
        border: '1px solid var(--border)',
        borderRadius: 6,
        background: 'var(--bg-input)',
      }}>
        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Your data · GDPR Article 15
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 6 }}>
          Download everything we have about you.
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Includes your profile, applications, assignments, time entries,
          transactions, payouts, deletion requests, and audit log entries
          where you took action. Juuni-internal analytical fields
          (reliability score, risk flags, internal notes) are excluded.
          Storage files (resume, avatar) are listed by path — contact
          support for a storage archive.
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={handleExportMyData}
            disabled={exporting}
            style={{
              padding: '8px 16px', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600,
              background: 'var(--text-primary)', color: 'var(--bg)',
              border: 'none', borderRadius: 6, cursor: exporting ? 'wait' : 'pointer',
            }}
          >
            {exporting ? 'Preparing…' : 'Download my data'}
          </button>
          {exportFeedback && (
            <span style={{
              fontSize: 12,
              fontFamily: 'var(--font-mono)',
              color: exportFeedback.ok ? 'var(--accent-teal)' : 'var(--text-error)',
            }}>
              {exportFeedback.msg}
            </span>
          )}
        </div>
      </div>

      {/* Phase 11 slot 4 — Rectification (GDPR Article 16) */}
      <div style={{
        marginTop: 24,
        padding: 20,
        border: '1px solid var(--border)',
        borderRadius: 6,
        background: 'var(--bg-input)',
      }}>
        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Rectification · GDPR Article 16
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 6 }}>
          Request a correction to a field you can&apos;t self-edit.
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
          Most identity fields (name, email, phone, bio, links, location)
          you can edit above directly. Use this form when the field is
          locked or you need an admin to verify a sensitive change.
          Admin reviews + applies within 30 days.
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 10 }}>
          <div>
            <label htmlFor="my-profile-rect-field" style={lbl}>Field</label>
            <select
              id="my-profile-rect-field"
              value={rectFieldName}
              onChange={e => setRectFieldName(e.target.value)}
              style={inp}
            >
              <option value="">— select —</option>
              {RECTIFICATION_ALLOWED_FIELDS.map(f => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="my-profile-rect-requested" style={lbl}>Requested value</label>
            <input
              id="my-profile-rect-requested"
              type="text"
              value={rectRequested}
              onChange={e => setRectRequested(e.target.value)}
              placeholder="What should it say?"
              style={inp}
            />
          </div>
        </div>
        <label htmlFor="my-profile-rect-reason" style={lbl}>Reason (optional)</label>
        <textarea
          id="my-profile-rect-reason"
          value={rectReason}
          onChange={e => setRectReason(e.target.value)}
          rows={2}
          placeholder="Why does this need to change?"
          style={{ ...inp, resize: 'vertical', marginBottom: 14 }}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            onClick={handleSubmitRectification}
            disabled={rectBusy || !rectFieldName || !rectRequested.trim()}
            style={{
              padding: '8px 16px', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600,
              background: 'var(--text-primary)', color: 'var(--bg)',
              border: 'none', borderRadius: 6, cursor: rectBusy ? 'wait' : 'pointer',
              opacity: (!rectFieldName || !rectRequested.trim()) ? 0.5 : 1,
            }}
          >
            {rectBusy ? 'Submitting…' : 'Submit request'}
          </button>
          {rectFeedback && (
            <span style={{
              fontSize: 12,
              fontFamily: 'var(--font-mono)',
              color: rectFeedback.ok ? 'var(--accent-teal)' : 'var(--text-error)',
            }}>
              {rectFeedback.msg}
            </span>
          )}
        </div>
        {ownRectifications.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>
              Your previous requests ({ownRectifications.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {ownRectifications.slice(0, 5).map(r => (
                <div key={r.id} style={{ fontSize: 12, fontFamily: 'var(--font-mono)', color: 'var(--text-secondary)' }}>
                  <span style={{ color: 'var(--text-primary)' }}>{r.field_name}</span>
                  <span> · {r.status}</span>
                  <span> · {new Date(r.requested_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Phase 11 slot 4 — Processing restriction (GDPR Article 18) */}
      <div style={{
        marginTop: 24,
        padding: 20,
        border: restriction ? '1px solid var(--accent-teal)' : '1px solid var(--border)',
        borderRadius: 6,
        background: restriction ? 'var(--bg-stat)' : 'var(--bg-input)',
      }}>
        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Processing restriction · GDPR Article 18
        </div>
        {restriction ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 6 }}>
              ✓ Your data is currently restricted from non-essential processing.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 6 }}>
              Restricted at {new Date(restriction.processing_restricted_at).toLocaleString()}
            </div>
            {restriction.processing_restriction_reason && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 14, fontStyle: 'italic' }}>
                Reason: {restriction.processing_restriction_reason}
              </div>
            )}
            <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
              Contact an admin to lift this restriction (e.g., once the dispute is resolved).
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 6 }}>
              Pause non-essential processing of your data.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Useful during a dispute or when you contest a record. Your data
              stays — admin manually honors the restriction outside of legal
              obligations (e.g., billing). Lift requires admin action.
            </div>
            <label htmlFor="my-profile-restriction-reason" style={lbl}>Reason (optional)</label>
            <input
              id="my-profile-restriction-reason"
              type="text"
              value={restrictionReason}
              onChange={e => setRestrictionReason(e.target.value)}
              placeholder="What's the dispute or context?"
              style={{ ...inp, marginBottom: 14 }}
            />
            <button
              onClick={handleRequestRestriction}
              disabled={restrictionBusy}
              style={{
                padding: '8px 16px', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600,
                background: 'var(--text-primary)', color: 'var(--bg)',
                border: 'none', borderRadius: 6, cursor: restrictionBusy ? 'wait' : 'pointer',
              }}
            >
              {restrictionBusy ? 'Submitting…' : 'Request restriction'}
            </button>
          </>
        )}
      </div>

      {/* Danger zone: account deletion (GDPR Article 17) */}
      <div style={{
        marginTop: 48,
        padding: 20,
        border: '1px solid var(--border-error)',
        borderRadius: 6,
        background: 'var(--bg-error)',
      }}>
        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--text-error)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 10 }}>
          Danger zone · Account deletion
        </div>
        {pendingDeletion ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 6 }}>
              Deletion requested {new Date(pendingDeletion.requested_at).toLocaleString()}.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 14 }}>
              An SPL will contact you to confirm. You can cancel any time before processing starts.
            </div>
            <button
              onClick={handleCancelDeletion}
              disabled={deletionBusy}
              style={{
                padding: '8px 16px', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600,
                background: 'var(--bg-input)', color: 'var(--text-primary)',
                border: '1px solid var(--border)', borderRadius: 6, cursor: deletionBusy ? 'wait' : 'pointer',
              }}
            >
              {deletionBusy ? 'Cancelling…' : 'Cancel request'}
            </button>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 6 }}>
              Permanently delete your Juuni account and personal data.
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
              Subject to a 30-day grace period. Your projects, time entries, earnings,
              and assignments will all be removed. Audit log entries are preserved
              (required by compliance retention).
            </div>
            <label htmlFor="my-profile-deletion-reason" style={{ ...lbl, marginTop: 4 }}>Reason (optional)</label>
            <textarea
              id="my-profile-deletion-reason"
              value={deletionReason}
              onChange={e => setDeletionReason(e.target.value)}
              rows={2}
              placeholder="Helps us improve — not required."
              style={{ ...inp, resize: 'vertical', marginBottom: 14 }}
            />
            <button
              onClick={handleRequestDeletion}
              disabled={deletionBusy}
              style={{
                padding: '8px 16px', fontSize: 12, fontFamily: 'var(--font-mono)', fontWeight: 600,
                background: 'var(--text-error)', color: 'var(--text-white)',
                border: 'none', borderRadius: 6, cursor: deletionBusy ? 'wait' : 'pointer',
              }}
            >
              {deletionBusy ? 'Submitting…' : 'Request account deletion'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}
